import { Fragment, startTransition, useRef } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';

import { AppsDocument } from 'src/graphql/actions/apps/apps.query';
import { withSuspense } from 'src/hoc';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';
import { getOwnedVariable, type IOwnershipFilter } from 'src/containers/Home/model';
import { Separator } from '@/components/ui/separator';

import { APPS_PER_PAGE, EMPTY_MESSAGE_BY_FILTER } from '../model';

import ApplicationRow from './ApplicationRow';
import ApplicationsListSkeleton, { ApplicationRowSkeleton } from './ApplicationsListSkeleton';

const LOAD_MORE_SKELETON_ROWS = 2;

type Props = {
  ownershipFilter: IOwnershipFilter;
  scrollAreaRef: React.RefObject<HTMLDivElement | null>;
};

const ApplicationsList = ({ ownershipFilter, scrollAreaRef }: Props) => {
  const {
    data: { apps },
    fetchMore,
  } = useSuspenseQuery(AppsDocument, {
    variables: { ...getOwnedVariable(ownershipFilter), page: 1, perPage: APPS_PER_PAGE },
  });

  const nextPage = Math.ceil(apps.nodes.length / APPS_PER_PAGE) + 1;
  const requestedPageRef = useRef<string | null>(null);

  const loadNextPage = () => {
    const pageKey = `${ownershipFilter}:${nextPage}`;

    if (requestedPageRef.current === pageKey) return Promise.resolve();

    requestedPageRef.current = pageKey;

    return new Promise<void>((resolve) => {
      startTransition(async () => {
        try {
          await fetchMore({
            variables: { ...getOwnedVariable(ownershipFilter), page: nextPage, perPage: APPS_PER_PAGE },
            updateQuery: (previous, { fetchMoreResult }) => ({
              ...previous,
              apps: { ...fetchMoreResult.apps, nodes: [...previous.apps.nodes, ...fetchMoreResult.apps.nodes] },
            }),
          });
        } catch {
          requestedPageRef.current = null;
        } finally {
          resolve();
        }
      });
    });
  };

  const { sentinelRef, isLoadingMore } = useInfiniteScroll({
    rootRef: scrollAreaRef,
    hasNextPage: apps.hasNextPage,
    onLoadMore: loadNextPage,
  });

  if (!apps.nodes.length) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">{EMPTY_MESSAGE_BY_FILTER[ownershipFilter]}</p>
    );
  }

  return (
    <Fragment>
      <ul>
        {apps.nodes.map((app, index) => (
          <Fragment key={app.id}>
            <ApplicationRow app={app} />
            {index < apps.nodes.length - 1 && (
              <li className="px-6">
                <Separator />
              </li>
            )}
          </Fragment>
        ))}
      </ul>
      {isLoadingMore && (
        <ul data-testid="load-more-skeleton" className="divide-y divide-border border-t border-border px-6">
          {Array.from({ length: LOAD_MORE_SKELETON_ROWS }, (_, index) => (
            <ApplicationRowSkeleton key={index} />
          ))}
        </ul>
      )}
      <div ref={sentinelRef} aria-hidden="true" />
    </Fragment>
  );
};

export default withSuspense(ApplicationsList, ApplicationsListSkeleton);
